import React from 'react';
interface UsageRateGraphProps {
  data: {
    day: string;
    actualBoxes: number;
    doctrineBoxes: number;
  }[];
  valueKey: string;
}
export const SubsistenceUsageRateGraph: React.FC<UsageRateGraphProps> = ({
  data,
  valueKey
}) => {
  const width = 500;
  const height = 200;
  const padding = 40;
  const mappedData = data.map(d => ({
    day: parseInt(d.day),
    actual: d.actualBoxes,
    doctrine: d.doctrineBoxes
  }));
  const xValues = mappedData.map(d => d.day);
  const yTicks = [0, 200, 400, 600, 800];
  const minX = Math.min(...xValues);
  const maxX = Math.max(...xValues);
  const minY = 0;
  const maxY = 800;
  const getX = (x: number) => (x - minX) / (maxX - minX) * (width - 2 * padding) + padding;
  const getY = (y: number) => height - padding - (y - minY) / (maxY - minY) * (height - 2 * padding);
  const actualPoints = mappedData.map(d => `${getX(d.day)},${getY(d.actual)}`).join(' ');
  const doctrinePoints = mappedData.map(d => `${getX(d.day)},${getY(d.doctrine)}`).join(' ');
  const barData = [{
    company: 'Alpha Company',
    value: 0.18
  }, {
    company: 'Bravo Company',
    value: 0.18
  }, {
    company: 'Charlie Company',
    value: 0.2
  }, {
    company: 'Delta Company',
    value: 0.175
  }, {
    company: 'Echo Company',
    value: 0.25
  }];
  const barChartHeight = 200;
  const barChartWidth = 600;
  const barPadding = 50;
  const barWidth = 40;
  const barGap = 30;
  const maxBarValue = 0.3;
  const totalBarWidth = barData.length * barWidth + (barData.length - 1) * barGap;
  const chartXEnd = barPadding + totalBarWidth;
  const getBarX = (index: number) => barPadding + index * (barWidth + barGap);
  const getBarY = (val: number) => barChartHeight - barPadding - val / maxBarValue * (barChartHeight - 2 * barPadding);
  return <div className="bg-white p-4 border border-gray-200 rounded-md w-full" data-id="element-729">
      <h3 className="text-lg font-semibold text-gray-700 mb-3" data-id="element-730">
        Daily Boxes vs Doctrine
      </h3>
      <div className="flex flex-row space-x-6 items-start w-full" data-id="element-731">
        {/* Left: Line Chart */}
        <div className="flex flex-row space-x-4 items-start w-full max-w-[600px]" data-id="element-732">
          <svg width={width} height={height} data-id="element-733">
            <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="#ccc" strokeWidth={1.5} data-id="element-734" />
            <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#ccc" strokeWidth={1.5} data-id="element-735" />
            {yTicks.map((tick, i) => {
            const y = getY(tick);
            return <g key={i} data-id="element-736">
                  <line x1={padding - 4} x2={padding} y1={y} y2={y} stroke="#999" data-id="element-737" />
                  <line x1={padding} x2={width - padding} y1={y} y2={y} stroke="#eee" data-id="element-738" />
                  <text x={padding - 8} y={y + 4} fontSize="14" fill="#666" textAnchor="end" data-id="element-739">
                    {tick}
                  </text>
                </g>;
          })}
            <text x={padding - 34} y={padding + (height - 2 * padding) / 2} textAnchor="middle" fontSize="14" fill="#666" transform={`rotate(-90 ${padding - 34},${padding + (height - 2 * padding) / 2})`} data-id="element-740">
              Boxes / day
            </text>
            {mappedData.map((d, i) => <text key={`tick-${i}`} x={getX(d.day)} y={height - padding + 18} fontSize="14" fill="#666" textAnchor="middle" data-id="element-741">
                {d.day} Day
              </text>)}
            <polyline points={doctrinePoints} fill="none" stroke="#bbb" strokeWidth={3} data-id="element-742" />
            <polyline points={actualPoints} fill="none" stroke="#000" strokeWidth={3} data-id="element-743" />
            {mappedData.map((d, i) => <circle key={`a-${i}`} cx={getX(d.day)} cy={getY(d.actual)} r={4} fill="#000" data-id="element-744" />)}
            {mappedData.map((d, i) => <circle key={`d-${i}`} cx={getX(d.day)} cy={getY(d.doctrine)} r={4} fill="#bbb" data-id="element-745" />)}
          </svg>
          <div className="text-amber-700 bg-amber-50 px-3 py-2 rounded-md border border-amber-200 text-sm w-[220px]" data-id="element-746">
            <span className="font-medium" data-id="element-747">Subsistence consumption rate</span> is
            trending ~15% above doctrine guidance. Consider revising assumptions
            accordingly.
          </div>
        </div>
        {/* Right: Bar Chart */}
        <div className="flex flex-row space-x-4 items-start w-[600px]" data-id="element-748">
          <svg width={barChartWidth} height={barChartHeight} data-id="element-749">
            {/* Y-axis */}
            <line x1={barPadding} y1={barPadding} x2={barPadding} y2={barChartHeight - barPadding} stroke="#ccc" strokeWidth={1.5} data-id="element-750" />
            {/* X-axis */}
            <line x1={barPadding} y1={barChartHeight - barPadding} x2={chartXEnd} y2={barChartHeight - barPadding} stroke="#ccc" strokeWidth={1.5} data-id="element-751" />
            {/* Y-axis ticks and labels */}
            {[0.1, 0.2, 0.3].map((tick, i) => {
            const y = getBarY(tick);
            return <g key={`ytick-${i}`} data-id="element-752">
                  <line x1={barPadding - 4} x2={barPadding} y1={y} y2={y} stroke="#999" data-id="element-753" />
                  <text x={barPadding - 8} y={y + 4} fontSize="14" fill="#666" textAnchor="end" data-id="element-754">
                    {tick.toFixed(2)}
                  </text>
                </g>;
          })}
            {/* Y-axis label */}
            <text x={barPadding - 34} y={barPadding + (barChartHeight - 2 * barPadding) / 2} textAnchor="middle" fontSize="14" fill="#666" transform={`rotate(-90 ${barPadding - 34},${barPadding + (barChartHeight - 2 * barPadding) / 2})`} data-id="element-755">
              Boxes / day
            </text>
            {/* Bars */}
            {barData.map((d, i) => {
            const barHeight = barChartHeight - barPadding - getBarY(d.value);
            const x = getBarX(i);
            return <g key={i} data-id="element-756">
                  <rect x={x} y={getBarY(d.value)} width={barWidth} height={barHeight} fill="#4B5563" data-id="element-757" />
                  <text x={x + barWidth / 2} y={barChartHeight - barPadding + 20} fontSize="14" textAnchor="middle" fill="#666" data-id="element-758">
                    {d.company.split(' ')[0]}
                  </text>
                </g>;
          })}
            {/* Doctrine Line (extended past bars) */}
            <line x1={barPadding} x2={barChartWidth - 10} y1={getBarY(0.18)} y2={getBarY(0.18)} stroke="#f59e0b" strokeWidth={2} strokeDasharray="4 2" data-id="element-759" />
            <text x={barChartWidth - 6} y={getBarY(0.18) - 4} textAnchor="start" fontSize="14" fill="#f59e0b" data-id="element-760">
              Doctrine
            </text>
          </svg>
          <div className="text-amber-700 bg-amber-50 px-3 py-2 rounded-md border border-amber-200 text-sm w-[220px]" data-id="element-761">
            <span className="font-medium" data-id="element-762">Charlie Company</span> and{' '}
            <span className="font-medium" data-id="element-763">Echo Company</span> have been
            consuming meals above doctrine guidance. Consider adjusting forecast
            accordingly.
          </div>
        </div>
      </div>
      {/* Shared Legend */}
      <div className="mt-4 flex justify-start space-x-6 text-sm text-gray-600" data-id="element-764">
        <div className="flex items-center space-x-2" data-id="element-765">
          <div className="w-4 h-1 bg-black" data-id="element-766"></div>
          <span data-id="element-767">Actual</span>
        </div>
        <div className="flex items-center space-x-2" data-id="element-768">
          <div className="w-4 h-1 bg-gray-400" data-id="element-769"></div>
          <span data-id="element-770">Doctrine</span>
        </div>
      </div>
    </div>;
};